"use client";

/**
 * The Computer button's state and handler, moved out of MessageInput verbatim
 * so the Dock's pill wears the same button by the same rule. PER-TASK: opening
 * in task A shows the panel only in task A's layout. Honest button (founder
 * 08-12): a dot while a browser is live or waking behind a closed panel; a
 * manual open lands on the surface the task actually uses, through the SAME
 * function the task-screen door's open_computer control runs (R-L8).
 */
import { useCallback, useRef } from "react";
import { useComputerStore } from "@/stores/computerStore";
import { useBrowserStore } from "@/stores/browserStore";
import { useTaskStore } from "@/stores/taskStore";
import { composerBrowserAffordance } from "@/lib/composer-browser-affordance";
import { getSidecarFrame } from "@/lib/frozen-frame-sidecar";
import { openComputerForTask } from "@/lib/open-computer-for-task";

export interface ComputerToggle {
  /** The pane is open for this task. */
  open: boolean;
  /** A live or waking browser behind a closed pane. */
  showDot: boolean;
  toggle: () => void;
}

export function useComputerToggle(
  currentTaskId: string | null
): ComputerToggle {
  const open = useComputerStore((s) =>
    currentTaskId ? s.isOpen(currentTaskId) : false
  );
  const browserViewState = useBrowserStore(
    (s) => s.viewSnapshot?.state ?? null
  );
  const taskAgentContext = useComputerStore((s) =>
    currentTaskId ? s.agentContext(currentTaskId) : null
  );
  const hasStoredBrowserFrame =
    useBrowserStore((s) =>
      currentTaskId ? Boolean(s.frozenFrameByTask[currentTaskId]) : false
    ) || (currentTaskId ? getSidecarFrame(currentTaskId) !== null : false);
  const affordance = composerBrowserAffordance({
    panelOpen: open,
    viewState: browserViewState,
    agentContext: taskAgentContext,
    hasStoredFrame: hasStoredBrowserFrame,
  });

  const cooldownRef = useRef(0);
  const toggle = useCallback(() => {
    const now = Date.now();
    if (now - cooldownRef.current < 200) return;
    cooldownRef.current = now;

    // On the new-task page `selectedTaskId` is null because the task has not
    // been created yet. Allocate the pending task UUID up front so the panel
    // has somewhere to mount; the first send turns that same UUID into the
    // real task (the client-UUID handoff the pre-warm already uses).
    let taskId = currentTaskId;
    if (!taskId) {
      const taskStore = useTaskStore.getState();
      taskId = taskStore.getOrAllocatePendingNewTaskId();
      taskStore._setSelectedTaskId(taskId);
    }
    if (open) useComputerStore.getState().close(taskId);
    else openComputerForTask(taskId);
  }, [open, currentTaskId]);

  return { open, showDot: affordance.showDot, toggle };
}
